import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TablePagination,
    TextField,
    Button,
    Typography,
    IconButton
} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import { v4 as uuid } from 'uuid';
import { AuthenticationService } from "../../../Services/AuthenticationService";
import { filterPersistenceService } from "../../../Services/FilterPersistenceService";
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Autocomplete from "@material-ui/lab/Autocomplete";

let globalProps;

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    filterToggle: {
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '2px 10px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        minWidth: '150px',
        '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#35d2d9',
            '&.Mui-checked': {
                color: '#35d2d9',
            },
        }
    },
    filterLabel: {
        marginLeft: '8px',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    statusCell: {
        cursor: 'pointer',
        userSelect: 'none',
        padding: '8px',
        textAlign: 'center',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }
    }
});

// Status color mapping based on Excel screenshot
const STATUS_COLORS = {
    '': '#ffffff', // White/blank
    'New Product': '#92d050', // Light green
    'Preorder Phase': '#00b0f0', // Bright blue
    'Future Product': '#ffd900', // Bright yellow
    'Updates': '#ff9900' // Orange
};

const STATUS_OPTIONS = ['', 'New Product', 'Preorder Phase', 'Future Product', 'Updates'];

class DealerSheet extends Component {
    state = {
        loading: true,
        user: {},
        assemblies: [],
        dealerSheetData: {}, // Map of assemblyId -> {status, notes, isExcluded}
        excludedAssemblyIds: new Set(), // Set of excluded assembly IDs
        selectedAssemblies: {},
        lastSelectedIndex: null,
        isProcessingModifierClick: false,
        page: 0,
        rowsPerPage: 100,
        searchTerm: '',
        selectedStatuses: [], // Array of selected status filters
        exclusionListDrawerOpen: false,
        lastExportDate: null,
        saving: false,
        downloading: false,
        showNewProducts: false,
        bulkStatusSelectKey: 0, // Key to force Select remount
        bulkStatusSelectValue: undefined // Track Select value
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadSavedFilterSettings();
        this.loadData();
    }

    loadSavedFilterSettings = () => {
        const savedSettings = filterPersistenceService.loadFilterSettings('dealersheet');
        if (savedSettings) {
            this.setState({
                page: savedSettings.page || 0,
                rowsPerPage: savedSettings.rowsPerPage || 100,
                searchTerm: savedSettings.searchTerm || '',
                selectedStatuses: savedSettings.selectedStatuses || [],
                showNewProducts: savedSettings.showNewProducts || false
            });
        }
    }

    saveFilterSettings = () => {
        const settings = {
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
            searchTerm: this.state.searchTerm,
            selectedStatuses: this.state.selectedStatuses,
            showNewProducts: this.state.showNewProducts
        };
        filterPersistenceService.saveFilterSettings('dealersheet', settings);
    }

    loadData = async () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('EditAssembliesAndParts')) {
            globalProps.history.push('/login');
            return;
        }

        self.setState({loading: true});

        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            
            // Load all assemblies expanded
            const assemblies = await assemblyService.getAllAssembliesExpanded();
            
            // Load dealer sheet data (status, notes, exclusion list, last export date)
            const dealerSheetData = await this.loadDealerSheetData();
            
            // Build excluded set
            const excludedSet = new Set();
            Object.keys(dealerSheetData).forEach(assemblyId => {
                if (dealerSheetData[assemblyId].isExcluded) {
                    excludedSet.add(assemblyId);
                }
            });

            self.setState({
                loading: false,
                user,
                assemblies,
                dealerSheetData,
                excludedAssemblyIds: excludedSet,
                lastExportDate: dealerSheetData._lastExportDate || null
            });
        } catch (error) {
            console.error('Error loading data:', error);
            self.setState({loading: false});
        }
    };

    loadDealerSheetData = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await axios.get(`${apiUrl}dealer-sheet/data`, {
                headers: {
                    'Authorization': `Bearer ${AuthenticationService.getInstance().getToken()}`
                }
            });
            return response.data || {};
        } catch (error) {
            console.error('Error loading dealer sheet data:', error);
            return {};
        }
    };

    saveDealerSheetData = async (updates) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            await axios.post(`${apiUrl}dealer-sheet/data`, updates, {
                headers: {
                    'Authorization': `Bearer ${AuthenticationService.getInstance().getToken()}`
                }
            });
        } catch (error) {
            console.error('Error saving dealer sheet data:', error);
            throw error;
        }
    };

    handleChangePage = (event, newPage) => {
        this.setState({page: newPage}, () => {
            this.saveFilterSettings();
        });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }, () => {
            this.saveFilterSettings();
        });
    };

    toggleShowNewProducts = () => {
        this.setState(prevState => ({
            showNewProducts: !prevState.showNewProducts,
            page: 0
        }), () => {
            this.saveFilterSettings();
        });
    };

    searchAssemblies = (event) => {
        const searchInput = event.target.value;
        this.setState({searchTerm: searchInput, page: 0}, () => {
            this.saveFilterSettings();
        });
    };

    getFilteredAssemblies = () => {
        let filtered = this.state.assemblies.filter(assembly => {
            // Only show assemblies without FinalOrderNumber
            if (assembly.assembly.finalOrderNumber) {
                return false;
            }

            // Filter out excluded assemblies
            if (this.state.excludedAssemblyIds.has(assembly.assembly.id)) {
                return false;
            }

            // Apply "Show New Products" filter - only show assemblies added since last export
            if (this.state.showNewProducts) {
                const assemblyData = this.state.dealerSheetData[assembly.assembly.id];
                const lastExportedDate = assemblyData?.lastExportedDate;
                // If assembly was never exported, it's new
                if (lastExportedDate) {
                    return false; // Skip assemblies that were already exported
                }
            }

            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerm = this.state.searchTerm.toLowerCase();
                const partNumber = `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`.toLowerCase();
                const description = (assembly.assembly.description || '').toLowerCase();
                if (!partNumber.includes(searchTerm) && !description.includes(searchTerm)) {
                    return false;
                }
            }

            // Apply status filter
            if (this.state.selectedStatuses.length > 0) {
                const assemblyStatus = this.getAssemblyStatus(assembly.assembly.id);
                if (!this.state.selectedStatuses.includes(assemblyStatus)) {
                    return false;
                }
            }

            return true;
        });

        // Sort like Assemblies.js: Driveshaft Carriers first, then others
        // Separate Driveshaft Carriers from other assemblies
        const driveshaftCarriersAssemblies = filtered.filter(x => x.assembly.assemblyType === 'Driveshaft Carriers').sort(function (a, b) {
            // Sort by manufacturerFor first
            if (a.assembly.manufacturerFor !== b.assembly.manufacturerFor) {
                if (a.assembly.manufacturerFor < b.assembly.manufacturerFor) return -1;
                if (a.assembly.manufacturerFor > b.assembly.manufacturerFor) return 1;
            }

            // Then by tertiaryType (empty/null first)
            const aTertiary = a.assembly.tertiaryType || '';
            const bTertiary = b.assembly.tertiaryType || '';
            if (aTertiary !== bTertiary) {
                if (aTertiary === '' && bTertiary !== '') return -1;
                if (aTertiary !== '' && bTertiary === '') return 1;
                if (aTertiary < bTertiary) return -1;
                if (aTertiary > bTertiary) return 1;
            }

            // Then by assemblyId
            if (a.assembly.assemblyId !== b.assembly.assemblyId) {
                if (a.assembly.assemblyId < b.assembly.assemblyId) return -1;
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
            }

            // Finally by revision (descending)
            if (a.assembly.revision !== b.assembly.revision) {
                if (a.assembly.revision > b.assembly.revision) return -1;
                if (a.assembly.revision < b.assembly.revision) return 1;
            }

            return 0;
        });

        const otherAssemblies = filtered.filter(x => x.assembly.assemblyType !== 'Driveshaft Carriers').sort(function (a, b) {
            // Sort by assemblyType first
            if (a.assembly.assemblyType !== b.assembly.assemblyType) {
                if (a.assembly.assemblyType < b.assembly.assemblyType) return -1;
                if (a.assembly.assemblyType > b.assembly.assemblyType) return 1;
            }

            // Then by manufacturerFor
            if (a.assembly.manufacturerFor !== b.assembly.manufacturerFor) {
                if (a.assembly.manufacturerFor < b.assembly.manufacturerFor) return -1;
                if (a.assembly.manufacturerFor > b.assembly.manufacturerFor) return 1;
            }

            // Then by tertiaryType (empty/null first)
            const aTertiary = a.assembly.tertiaryType || '';
            const bTertiary = b.assembly.tertiaryType || '';
            if (aTertiary !== bTertiary) {
                if (aTertiary === '' && bTertiary !== '') return -1;
                if (aTertiary !== '' && bTertiary === '') return 1;
                if (aTertiary < bTertiary) return -1;
                if (aTertiary > bTertiary) return 1;
            }

            // Then by assemblyId
            if (a.assembly.assemblyId !== b.assembly.assemblyId) {
                if (a.assembly.assemblyId < b.assembly.assemblyId) return -1;
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
            }

            // Finally by revision (descending)
            if (a.assembly.revision !== b.assembly.revision) {
                if (a.assembly.revision > b.assembly.revision) return -1;
                if (a.assembly.revision < b.assembly.revision) return 1;
            }

            return 0;
        });

        // Combine with Driveshaft Carriers first
        filtered = [...driveshaftCarriersAssemblies, ...otherAssemblies];

        return filtered;
    };

    getAssemblyStatus = (assemblyId) => {
        return this.state.dealerSheetData[assemblyId]?.status || '';
    };

    getAssemblyNotes = (assemblyId) => {
        return this.state.dealerSheetData[assemblyId]?.notes || '';
    };

    cycleStatus = (assemblyId, event) => {
        event.stopPropagation();
        const currentStatus = this.getAssemblyStatus(assemblyId);
        const currentIndex = STATUS_OPTIONS.indexOf(currentStatus);
        const nextIndex = (currentIndex + 1) % STATUS_OPTIONS.length;
        const nextStatus = STATUS_OPTIONS[nextIndex];

        this.updateAssemblyStatus(assemblyId, nextStatus);
    };

    updateAssemblyStatus = async (assemblyId, status) => {
        const dealerSheetData = { ...this.state.dealerSheetData };
        if (!dealerSheetData[assemblyId]) {
            dealerSheetData[assemblyId] = {};
        }
        dealerSheetData[assemblyId].status = status;

        this.setState({ dealerSheetData });

        try {
            await this.saveDealerSheetData({ [assemblyId]: dealerSheetData[assemblyId] });
        } catch (error) {
            console.error('Error updating status:', error);
            // Revert on error
            const reverted = { ...this.state.dealerSheetData };
            reverted[assemblyId] = { ...reverted[assemblyId] };
            if (reverted[assemblyId].status === status) {
                delete reverted[assemblyId].status;
            }
            this.setState({ dealerSheetData: reverted });
        }
    };

    updateAssemblyNotes = async (assemblyId, notes) => {
        const dealerSheetData = { ...this.state.dealerSheetData };
        if (!dealerSheetData[assemblyId]) {
            dealerSheetData[assemblyId] = {};
        }
        dealerSheetData[assemblyId].notes = notes;

        this.setState({ dealerSheetData });

        try {
            await this.saveDealerSheetData({ [assemblyId]: dealerSheetData[assemblyId] });
        } catch (error) {
            console.error('Error updating notes:', error);
        }
    };

    handleCheckboxClick = (event, assemblyId) => {
        const filteredAssemblies = this.getFilteredAssemblies();
        const currentIndex = filteredAssemblies.findIndex(a => a.assembly.id === assemblyId);
        const isCurrentlySelected = !!this.state.selectedAssemblies[assemblyId];
        
        // Check for modifier keys
        const isCtrlPressed = (event && (event.ctrlKey || event.metaKey)) || 
                             (event.nativeEvent && (event.nativeEvent.ctrlKey || event.nativeEvent.metaKey));
        const isShiftPressed = (event && event.shiftKey) || 
                               (event.nativeEvent && event.nativeEvent.shiftKey);
        
        // Set flag to prevent checkbox onChange from processing modifier clicks
        const hasModifiers = isCtrlPressed || isShiftPressed;
        if (hasModifiers) {
            this.setState({ isProcessingModifierClick: true });
            if (event && event.preventDefault) event.preventDefault();
            if (event && event.stopPropagation) event.stopPropagation();
        }
        
        if (currentIndex === -1) {
            // Assembly not found in filtered data, fall back to simple toggle
            const selectedAssemblies = { ...this.state.selectedAssemblies };
            if (isCurrentlySelected) {
                delete selectedAssemblies[assemblyId];
            } else {
                selectedAssemblies[assemblyId] = true;
            }
            this.setState({ 
                selectedAssemblies,
                lastSelectedIndex: null
            });
            if (hasModifiers) {
                setTimeout(() => {
                    this.setState({ isProcessingModifierClick: false });
                }, 100);
            }
            return;
        }

        if (isShiftPressed && this.state.lastSelectedIndex !== null && this.state.lastSelectedIndex !== -1) {
            // Shift+Click: Select range from lastSelectedIndex to currentIndex
            const startIndex = Math.min(this.state.lastSelectedIndex, currentIndex);
            const endIndex = Math.max(this.state.lastSelectedIndex, currentIndex);
            const rangeIds = filteredAssemblies.slice(startIndex, endIndex + 1).map(a => a.assembly.id);
            
            if (isCtrlPressed) {
                // Ctrl+Shift+Click: Toggle range selection
                const allInRangeSelected = rangeIds.every(id => !!this.state.selectedAssemblies[id]);
                const selectedAssemblies = { ...this.state.selectedAssemblies };
                if (allInRangeSelected) {
                    // Remove all items in range
                    rangeIds.forEach(id => delete selectedAssemblies[id]);
                } else {
                    // Add all items in range
                    rangeIds.forEach(id => selectedAssemblies[id] = true);
                }
                this.setState({
                    selectedAssemblies,
                    lastSelectedIndex: currentIndex
                });
            } else {
                // Shift+Click: Add range, or remove range when anchor/range indicates deselection flow
                const selectedAssemblies = { ...this.state.selectedAssemblies };
                const anchorId = filteredAssemblies[this.state.lastSelectedIndex]?.assembly?.id;
                const anchorIsSelected = anchorId ? !!this.state.selectedAssemblies[anchorId] : true;
                const allInRangeSelected = rangeIds.every(id => !!this.state.selectedAssemblies[id]);
                const shouldDeselectRange = !anchorIsSelected || (isCurrentlySelected && allInRangeSelected);

                if (shouldDeselectRange) {
                    rangeIds.forEach(id => delete selectedAssemblies[id]);
                } else {
                    rangeIds.forEach(id => selectedAssemblies[id] = true);
                }

                this.setState({
                    selectedAssemblies,
                    lastSelectedIndex: currentIndex
                });
            }
        } else if (isCtrlPressed) {
            // Ctrl+Click: Toggle individual item
            const selectedAssemblies = { ...this.state.selectedAssemblies };
            if (isCurrentlySelected) {
                delete selectedAssemblies[assemblyId];
            } else {
                selectedAssemblies[assemblyId] = true;
            }
            this.setState({
                selectedAssemblies,
                lastSelectedIndex: currentIndex
            });
        } else {
            // Normal click: Simple toggle
            const selectedAssemblies = { ...this.state.selectedAssemblies };
            if (isCurrentlySelected) {
                delete selectedAssemblies[assemblyId];
            } else {
                selectedAssemblies[assemblyId] = true;
            }
            this.setState({
                selectedAssemblies,
                lastSelectedIndex: currentIndex
            });
        }
        
        // Clear the modifier flag after a short delay
        if (hasModifiers) {
            setTimeout(() => {
                this.setState({ isProcessingModifierClick: false });
            }, 100);
        }
    };

    deselectAll = () => {
        this.setState({ selectedAssemblies: {} });
    };

    handleBulkStatusChange = async (status) => {
        const selectedIds = Object.keys(this.state.selectedAssemblies);
        if (selectedIds.length === 0) return;

        console.log('handleBulkStatusChange called with status:', status, 'selectedIds:', selectedIds);

        // Explicitly handle empty string - status can be '' to clear
        const newStatus = status === '' ? '' : status;

        const updates = {};
        const dealerSheetData = { ...this.state.dealerSheetData };

        selectedIds.forEach(assemblyId => {
            if (!dealerSheetData[assemblyId]) {
                dealerSheetData[assemblyId] = {};
            }
            // Set status explicitly (empty string to clear)
            dealerSheetData[assemblyId].status = newStatus;
            // Ensure we include all existing properties
            updates[assemblyId] = {
                ...dealerSheetData[assemblyId],
                status: newStatus
            };
        });

        console.log('Updates to send:', updates);

        this.setState({ dealerSheetData });

        try {
            await this.saveDealerSheetData(updates);
            console.log('Status saved successfully');
            // Reload data to ensure UI is in sync
            await this.loadDealerSheetData();
        } catch (error) {
            console.error('Error updating bulk status:', error);
            // Revert on error by reloading original data
            await this.loadDealerSheetData();
        }
    };

    handleBulkNotesChange = async (notes) => {
        const selectedIds = Object.keys(this.state.selectedAssemblies);
        if (selectedIds.length === 0) return;

        const updates = {};
        const dealerSheetData = { ...this.state.dealerSheetData };

        selectedIds.forEach(assemblyId => {
            if (!dealerSheetData[assemblyId]) {
                dealerSheetData[assemblyId] = {};
            }
            dealerSheetData[assemblyId].notes = notes;
            updates[assemblyId] = dealerSheetData[assemblyId];
        });

        this.setState({ dealerSheetData });

        try {
            await this.saveDealerSheetData(updates);
        } catch (error) {
            console.error('Error updating bulk notes:', error);
        }
    };

    handleBulkExclude = async () => {
        const selectedIds = Object.keys(this.state.selectedAssemblies);
        if (selectedIds.length === 0) return;

        if (!window.confirm(`Exclude ${selectedIds.length} assembly(ies) from the dealer sheet?`)) {
            return;
        }

        const excludedSet = new Set(this.state.excludedAssemblyIds);
        const updates = {};
        const dealerSheetData = { ...this.state.dealerSheetData };

        selectedIds.forEach(assemblyId => {
            excludedSet.add(assemblyId);
            if (!dealerSheetData[assemblyId]) {
                dealerSheetData[assemblyId] = {};
            }
            dealerSheetData[assemblyId].isExcluded = true;
            updates[assemblyId] = dealerSheetData[assemblyId];
        });

        this.setState({ 
            excludedAssemblyIds: excludedSet,
            dealerSheetData,
            selectedAssemblies: {}
        });

        try {
            await this.saveDealerSheetData(updates);
        } catch (error) {
            console.error('Error excluding assemblies:', error);
        }
    };

    openExclusionListDrawer = () => {
        this.setState({ exclusionListDrawerOpen: true });
    };

    closeExclusionListDrawer = () => {
        this.setState({ exclusionListDrawerOpen: false });
    };

    removeFromExclusionList = async (assemblyId) => {
        const excludedSet = new Set(this.state.excludedAssemblyIds);
        excludedSet.delete(assemblyId);

        const dealerSheetData = { ...this.state.dealerSheetData };
        if (dealerSheetData[assemblyId]) {
            dealerSheetData[assemblyId].isExcluded = false;
        }

        this.setState({ 
            excludedAssemblyIds: excludedSet,
            dealerSheetData
        });

        try {
            await this.saveDealerSheetData({ [assemblyId]: dealerSheetData[assemblyId] || { isExcluded: false } });
        } catch (error) {
            console.error('Error removing from exclusion list:', error);
        }
    };

    handleFinishAndDownload = async () => {
        const self = this;
        self.setState({ saving: true, downloading: true });

        try {
            // Update last export date
            const exportDate = new Date().toISOString();
            const apiUrl = process.env.REACT_APP_API_URL || '';
            await axios.post(`${apiUrl}dealer-sheet/last-export`, { date: exportDate }, {
                headers: {
                    'Authorization': `Bearer ${AuthenticationService.getInstance().getToken()}`
                }
            });

            // Generate and download Excel file
            const response = await axios.get(`${apiUrl}dealer-sheet/export-excel`, {
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${AuthenticationService.getInstance().getToken()}`
                }
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `JXB Tiered Wholesale Pricing - ${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            self.setState({ 
                saving: false, 
                downloading: false,
                lastExportDate: exportDate
            });

            window.$alertSuccess('Dealer sheet exported successfully');
        } catch (error) {
            console.error('Error exporting dealer sheet:', error);
            self.setState({ saving: false, downloading: false });
            window.$alertError('Error exporting dealer sheet');
        }
    };

    getStatusColor = (status) => {
        return STATUS_COLORS[status] || STATUS_COLORS[''];
    };

    render() {
        const { classes } = this.props;
        const filteredAssemblies = this.getFilteredAssemblies();
        const selectedCount = Object.keys(this.state.selectedAssemblies).length;
        const paginatedAssemblies = filteredAssemblies.slice(
            this.state.page * this.state.rowsPerPage,
            this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
        );

        if (this.state.loading) {
            return (
                <DashboardTemplate title="Dealer Sheet" onSyncDatabase={this.loadData}>
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px'}}>
                        <CircularProgress/>
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="Dealer Sheet" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container" style={{margin: '0 auto 20px', maxWidth: 1900}}>
                    {/* Toolbar */}
                    <div className={classes.toolbar} style={{marginBottom: '20px'}}>
                        <div className={classes.toolbarLeft} style={{gap: '16px', flexWrap: 'wrap'}}>
                            <TextField
                                placeholder="Search by PN or Description..."
                                variant="outlined"
                                size="small"
                                value={this.state.searchTerm}
                                onChange={this.searchAssemblies}
                                style={{minWidth: '250px'}}
                            />
                            
                            <FormControl variant="outlined" size="small" style={{minWidth: '200px'}}>
                                <InputLabel>Filter by Status</InputLabel>
                                <Select
                                    multiple
                                    value={this.state.selectedStatuses}
                                    onChange={(e) => {
                                        this.setState({ selectedStatuses: e.target.value, page: 0 }, () => {
                                            this.saveFilterSettings();
                                        });
                                    }}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) return 'All Statuses';
                                        if (selected.length === 1) return selected[0] || '(none)';
                                        return `${selected.length} selected`;
                                    }}
                                >
                                    <MenuItem value="">(none)</MenuItem>
                                    <MenuItem value="New Product">New Product</MenuItem>
                                    <MenuItem value="Preorder Phase">Preorder Phase</MenuItem>
                                    <MenuItem value="Future Product">Future Product</MenuItem>
                                    <MenuItem value="Updates">Updates</MenuItem>
                                </Select>
                            </FormControl>

                            {selectedCount > 0 && (
                                <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap'}}>
                                    <Typography variant="body2" style={{marginRight: '4px'}}>
                                        {selectedCount} selected
                                    </Typography>
                                    <FormControl variant="outlined" size="small" style={{minWidth: '150px'}}>
                                        <InputLabel>Set Status</InputLabel>
                                        <Select
                                            value={this.state.bulkStatusSelectValue !== undefined ? this.state.bulkStatusSelectValue : ""}
                                            onChange={(e) => {
                                                const newStatus = e.target.value;
                                                console.log('Select onChange fired with value:', newStatus);
                                                // Handle the status change
                                                this.handleBulkStatusChange(newStatus);
                                                // Reset select value after processing
                                                setTimeout(() => {
                                                    this.setState({ bulkStatusSelectValue: undefined });
                                                }, 0);
                                            }}
                                            onOpen={() => {
                                                // When opening, set to a temporary value so selecting "(none)" will be a change
                                                if (this.state.bulkStatusSelectValue === undefined || this.state.bulkStatusSelectValue === "") {
                                                    this.setState({ bulkStatusSelectValue: "__temp__" });
                                                }
                                            }}
                                            onClose={() => {
                                                // Reset select value when dropdown closes
                                                setTimeout(() => {
                                                    this.setState({ bulkStatusSelectValue: undefined });
                                                }, 100);
                                            }}
                                        >
                                            <MenuItem value="">(none)</MenuItem>
                                            <MenuItem value="New Product">New Product</MenuItem>
                                            <MenuItem value="Preorder Phase">Preorder Phase</MenuItem>
                                            <MenuItem value="Future Product">Future Product</MenuItem>
                                            <MenuItem value="Updates">Updates</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        placeholder="Set Notes..."
                                        variant="outlined"
                                        size="small"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                this.handleBulkNotesChange(e.target.value);
                                                e.target.value = '';
                                            }
                                        }}
                                        style={{minWidth: '200px'}}
                                    />
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        onClick={this.handleBulkExclude}
                                    >
                                        Exclude Selected
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={this.deselectAll}
                                    >
                                        Clear Selection
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className={classes.toolbarRight}>
                            <div className={classes.filterToggle} onClick={this.toggleShowNewProducts}>
                                <Checkbox
                                    checked={this.state.showNewProducts}
                                    onChange={this.toggleShowNewProducts}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <span className={classes.filterLabel}>Show New Since Last Export</span>
                            </div>
                            <Button
                                variant="outlined"
                                onClick={this.openExclusionListDrawer}
                            >
                                Exclusion List ({this.state.excludedAssemblyIds.size})
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={this.handleFinishAndDownload}
                                disabled={this.state.saving || this.state.downloading}
                                className="success-button-lg"
                            >
                                {this.state.downloading ? (
                                    <>
                                        <CircularProgress size={20} style={{marginRight: '8px'}} />
                                        Exporting...
                                    </>
                                ) : (
                                    'Finish & Download'
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Last Export Date */}
                    {this.state.lastExportDate && (
                        <div style={{marginBottom: '10px', fontSize: '14px', color: '#666'}}>
                            Last Export: {new Date(this.state.lastExportDate).toLocaleDateString()}
                        </div>
                    )}

                    {/* Table */}
                    <div className="parts-table-container">
                        <table className="parts-table">
                            <thead>
                                <tr>
                                    <th style={{width: '50px', textAlign: 'center'}}>
                                        <Checkbox
                                            checked={paginatedAssemblies.length > 0 && paginatedAssemblies.every(a => this.state.selectedAssemblies[a.assembly.id])}
                                            indeterminate={paginatedAssemblies.some(a => this.state.selectedAssemblies[a.assembly.id]) && !paginatedAssemblies.every(a => this.state.selectedAssemblies[a.assembly.id])}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const selected = {};
                                                    paginatedAssemblies.forEach(a => {
                                                        selected[a.assembly.id] = true;
                                                    });
                                                    this.setState({ selectedAssemblies: { ...this.state.selectedAssemblies, ...selected } });
                                                } else {
                                                    const selected = { ...this.state.selectedAssemblies };
                                                    paginatedAssemblies.forEach(a => {
                                                        delete selected[a.assembly.id];
                                                    });
                                                    this.setState({ selectedAssemblies: selected });
                                                }
                                            }}
                                        />
                                    </th>
                                    <th style={{minWidth: '100px'}}>Status</th>
                                    <th style={{minWidth: '150px'}}>PN</th>
                                    <th style={{minWidth: '700px'}}>Description</th>
                                    <th style={{minWidth: '500px'}}>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    let lastAssemblyType = null;
                                    let lastManufacturerFor = null;
                                    let lastTertiaryType = null;
                                    const self = this;

                                    return paginatedAssemblies.map((assemblyExpanded) => {
                                        const assembly = assemblyExpanded.assembly;
                                        const status = this.getAssemblyStatus(assembly.id);
                                        const notes = this.getAssemblyNotes(assembly.id);
                                        const statusColor = this.getStatusColor(status);
                                        const isSelected = !!this.state.selectedAssemblies[assembly.id];

                                        // Category headers
                                        let partTypeHeader = [];
                                        let manufacturerForHeader = [];
                                        let tertiaryTypeHeader = [];

                                        if (assembly.assemblyType !== lastAssemblyType) {
                                            const currentAssemblyType = assembly.assemblyType;
                                            partTypeHeader = [<tr key={`type-${assembly.assemblyType}`} className="dealer-sheet-part-type-header">
                                                <td colSpan={5} style={{
                                                    backgroundColor: '#000',
                                                    color: '#FFF',
                                                    fontWeight: 'bold',
                                                    padding: '2px 4px !important',
                                                    borderBottom: '2px solid #fff',
                                                    cursor: 'pointer'
                                                }} className='text-left'>
                                                    {assembly.assemblyType}
                                                </td>
                                            </tr>];
                                            lastAssemblyType = assembly.assemblyType;
                                        }

                                        if (assembly.manufacturerFor !== lastManufacturerFor) {
                                            const currentManufacturerFor = assembly.manufacturerFor;
                                            manufacturerForHeader = [<tr key={`manufacturer-${assembly.manufacturerFor}`} className="dealer-sheet-manufacturer-header">
                                                <td colSpan={5} style={{
                                                    backgroundColor: '#767676',
                                                    color: '#FFF',
                                                    fontWeight: 'bold',
                                                    padding: '2px 4px !important',
                                                    cursor: 'pointer'
                                                }} className='text-left'>
                                                    {assembly.manufacturerFor || '\u00A0'}
                                                </td>
                                            </tr>];
                                            lastManufacturerFor = assembly.manufacturerFor;
                                        }

                                        if (assembly.tertiaryType !== lastTertiaryType) {
                                            if (assembly.tertiaryType && assembly.tertiaryType.trim() !== '') {
                                                const currentTertiaryType = assembly.tertiaryType;
                                                tertiaryTypeHeader = [<tr key={`tertiary-${assembly.tertiaryType}`} className="dealer-sheet-tertiary-type-header">
                                                    <td colSpan={5} style={{
                                                        backgroundColor: '#767676',
                                                        color: '#000',
                                                        fontWeight: 'bold',
                                                        padding: '2px 4px !important',
                                                        borderBottom: '1px solid #ccc',
                                                        cursor: 'pointer'
                                                    }} className='text-left'>
                                                        {assembly.tertiaryType}
                                                    </td>
                                                </tr>];
                                            } else if (lastTertiaryType && lastTertiaryType.trim() !== '') {
                                                tertiaryTypeHeader = [<tr key={`tertiary-clear`} className="dealer-sheet-tertiary-type-header">
                                                    <td colSpan={5} style={{
                                                        backgroundColor: 'transparent',
                                                        height: '1px',
                                                        padding: '0',
                                                        borderBottom: 'none'
                                                    }} className='text-left'>
                                                        &nbsp;
                                                    </td>
                                                </tr>];
                                            }
                                            lastTertiaryType = assembly.tertiaryType;
                                        }

                                        return (
                                            <React.Fragment key={assembly.id}>
                                                {partTypeHeader}
                                                {manufacturerForHeader}
                                                {tertiaryTypeHeader}
                                                <tr 
                                                    style={{
                                                        borderLeft: `4px solid ${statusColor}`,
                                                        backgroundColor: isSelected ? '#e3f2fd' : 'white'
                                                    }}
                                                >
                                                    <td style={{textAlign: 'center'}}>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                if (!this.state.isProcessingModifierClick) {
                                                                    this.handleCheckboxClick(e, assembly.id);
                                                                }
                                                            }}
                                                            onClick={(e) => {
                                                                const isCtrlPressed = e.ctrlKey || e.metaKey;
                                                                const isShiftPressed = e.shiftKey;
                                                                if (isCtrlPressed || isShiftPressed) {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    this.handleCheckboxClick(e, assembly.id);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td 
                                                        className={classes.statusCell}
                                                        onClick={(e) => this.cycleStatus(assembly.id, e)}
                                                        title="Click to cycle status"
                                                    >
                                                        {status || ''}
                                                    </td>
                                                    <td 
                                                        className="text-left"
                                                        style={{
                                                            color: assembly.isDiscontinued ? '#ff0000' : 'inherit',
                                                            textDecoration: assembly.isDiscontinued ? 'line-through' : 'none'
                                                        }}
                                                    >
                                                        {assembly.assemblyId} rev {assembly.revision}
                                                    </td>
                                                    <td className="text-left description-ellipsis">
                                                        {assembly.description || ''}
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            variant="outlined"
                                                            size="small"
                                                            value={notes}
                                                            onChange={(e) => {
                                                                const newNotes = e.target.value;
                                                                this.updateAssemblyNotes(assembly.id, newNotes);
                                                            }}
                                                            onBlur={() => {
                                                                // Notes are saved on change, but we can add debouncing if needed
                                                            }}
                                                            style={{width: '100%'}}
                                                            inputProps={{maxLength: 500}}
                                                        />
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px'}}>
                        <TablePagination
                            component="div"
                            count={filteredAssemblies.length}
                            page={this.state.page}
                            onPageChange={this.handleChangePage}
                            rowsPerPage={this.state.rowsPerPage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                            rowsPerPageOptions={[25, 50, 100, 200]}
                            ActionsComponent={(props) => (
                                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <IconButton
                                        onClick={() => props.onPageChange(null, 0)}
                                        disabled={props.page === 0}
                                    >
                                        <FirstPageIcon/>
                                    </IconButton>
                                    <IconButton
                                        onClick={() => props.onPageChange(null, props.page - 1)}
                                        disabled={props.page === 0}
                                    >
                                        <ChevronLeftIcon/>
                                    </IconButton>
                                    <IconButton
                                        onClick={() => props.onPageChange(null, props.page + 1)}
                                        disabled={props.page >= Math.ceil(props.count / props.rowsPerPage) - 1}
                                    >
                                        <ChevronRightIcon/>
                                    </IconButton>
                                    <IconButton
                                        onClick={() => props.onPageChange(null, Math.max(0, Math.ceil(props.count / props.rowsPerPage) - 1))}
                                        disabled={props.page >= Math.ceil(props.count / props.rowsPerPage) - 1}
                                    >
                                        <LastPageIcon/>
                                    </IconButton>
                                </div>
                            )}
                        />
                    </div>
                </div>

                {/* Exclusion List Drawer */}
                {this.state.exclusionListDrawerOpen && (
                    <DrawerMenuRight
                        title="Exclusion List"
                        customWidth={1000}
                        open={this.state.exclusionListDrawerOpen}
                        onClose={this.closeExclusionListDrawer}
                    >
                        <div style={{padding: '20px'}}>
                            <Typography variant="body1" style={{marginBottom: '20px'}}>
                                {this.state.excludedAssemblyIds.size} assembly(ies) excluded from dealer sheet
                            </Typography>
                            
                            <div className="parts-table-container">
                                <table className="parts-table">
                                    <thead>
                                        <tr>
                                            <th style={{minWidth: '200px'}}>PN</th>
                                            <th style={{minWidth: '300px'}}>Description</th>
                                            <th style={{minWidth: '200px'}}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.assemblies
                                            .filter(a => this.state.excludedAssemblyIds.has(a.assembly.id))
                                            .sort((a, b) => {
                                                if (a.assembly.assemblyId < b.assembly.assemblyId) return -1;
                                                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                                                return 0;
                                            })
                                            .map((assemblyExpanded) => {
                                                const assembly = assemblyExpanded.assembly;
                                                const status = this.getAssemblyStatus(assembly.id);

                                                return (
                                                    <tr key={assembly.id}>
                                                        <td className="text-left">
                                                            {assembly.assemblyId} rev {assembly.revision}
                                                        </td>
                                                        <td className="text-left description-ellipsis">
                                                            {assembly.description || ''}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => this.removeFromExclusionList(assembly.id)}
                                                            >
                                                                Remove from Exclusion
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </DrawerMenuRight>
                )}
                </div>
            </DashboardTemplate>
        );
    }
}

DealerSheet.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(DealerSheet);

